/**
 * @ais/dashboard-widgets · DashboardWidgetHost
 *
 * Parallel to `@ais/plugin-host`'s `PluginHost` but for `DashboardWidget`
 * bundles. The supervision dashboard constructs one host, registers the
 * built-in widget types, then optionally `loadFromRegistry()`s to pull
 * runtime widgets out of `/plugins/registry.json` (filtered to
 * `kind === 'dashboard-widget'`).
 *
 * Bundles are ES modules whose default export is a factory
 * `() => DashboardWidget`. Like viewer plugins, they should externalise
 * `three`, `@ais/viewer-core`, `@ais/viewer-ui`, `@ais/dashboard-widgets`
 * via the dashboard's importmap so the host's module instances are reused.
 */
import {
  type PluginManifest,
  type PluginRegistry,
  type PluginRegistryEntry,
  satisfies,
  validateManifest,
} from '@ais/plugin-host';

import type { DashboardWidget } from './Widget';

export interface DashboardWidgetHostOptions {
  /** Absolute URL of the registry index. Defaults to '/plugins/registry.json'. */
  registryUrl?: string;
  /** Engine version checked against manifest.engines['@ais/dashboard-widgets']. */
  engineVersion: string;
}

const DEFAULT_REGISTRY_URL = '/plugins/registry.json';

export interface DashboardWidgetLoadResult {
  loaded: string[];
  failed: string[];
}

/** Where a registered widget came from. Used by hosts that want to group
 *  built-in vs marketplace widgets in their type pickers. */
export type DashboardWidgetSource = 'builtin' | 'marketplace';

export class DashboardWidgetHost {
  private readonly registryUrl: string;
  private readonly engineVersion: string;
  private registryCache: PluginRegistry | null = null;
  private widgets: Map<string, DashboardWidget> = new Map();
  private sources: Map<string, DashboardWidgetSource> = new Map();

  constructor(opts: DashboardWidgetHostOptions) {
    this.registryUrl = opts.registryUrl ?? DEFAULT_REGISTRY_URL;
    this.engineVersion = opts.engineVersion;
  }

  /** Register a widget. Idempotent: re-registering an existing id is a no-op.
   *  `source` defaults to 'builtin'; `loadFromRegistry` passes 'marketplace'. */
  register(widget: DashboardWidget, source: DashboardWidgetSource = 'builtin'): void {
    if (this.widgets.has(widget.id)) return;
    this.widgets.set(widget.id, widget);
    this.sources.set(widget.id, source);
  }

  get(id: string): DashboardWidget | undefined {
    return this.widgets.get(id);
  }

  /** Source-of-record for a registered widget. */
  getSource(id: string): DashboardWidgetSource | undefined {
    return this.sources.get(id);
  }

  list(): DashboardWidget[] {
    return [...this.widgets.values()];
  }

  /** Fetch the marketplace registry, filtered to dashboard-widget entries. */
  async listAvailable(force = false): Promise<PluginRegistryEntry[]> {
    if (!this.registryCache || force) {
      // 'no-cache' = always revalidate. 'default' let browsers heuristically
      // cache the registry, and a profile that fetched it BEFORE a widget was
      // synced kept showing "Unknown widget type" through every soft reload
      // (live 2026-08-25, survived even server-side no-cache headers because
      // the stale copy predated them). With ETags this costs a 304, not bytes.
      const r = await fetch(this.registryUrl, { cache: force ? 'reload' : 'no-cache' });
      if (!r.ok) throw new Error(`registry fetch ${this.registryUrl} → HTTP ${r.status}`);
      const json = (await r.json()) as PluginRegistry;
      if (!json || !Array.isArray(json.plugins)) {
        throw new Error('registry.json malformed: expected { plugins: [...] }');
      }
      this.registryCache = json;
    }
    return this.registryCache.plugins.filter((e) => e.kind === 'dashboard-widget');
  }

  /**
   * For each dashboard-widget entry in the registry: fetch the manifest,
   * validate engine compatibility, dynamic-import the bundle, register the
   * widget. Failures are logged and skipped — one broken widget shouldn't
   * sink the rest. Returns the ids that succeeded vs failed.
   */
  async loadFromRegistry(): Promise<DashboardWidgetLoadResult> {
    const loaded: string[] = [];
    const failed: string[] = [];
    let entries: PluginRegistryEntry[];
    try {
      entries = await this.listAvailable();
    } catch (e) {
      console.warn(`[dashboard-widgets] registry fetch failed: ${(e as Error).message}`);
      return { loaded, failed };
    }

    for (const entry of entries) {
      if (this.widgets.has(entry.id)) continue;
      try {
        // A RELATIVE manifestUrl resolves against the REGISTRY, not the page —
        // the rule PluginHost.resolveFromRegistry already follows. A host that
        // serves its own mirror emits relative entries precisely so the file
        // works under any mount, and resolving those against the page turned
        // every marketplace widget into a 404 on a data hub: five cells that
        // said "Unknown widget type" while the bundles sat on disk.
        const manifestUrl = this.resolveFromRegistry(entry.manifestUrl);
        const manifest = await this.fetchManifest(manifestUrl);
        this.assertCompatible(manifest);
        const moduleUrl = absoluteUrl(manifest.entryUrl, manifestUrl);
        const mod = (await import(/* @vite-ignore */ moduleUrl)) as {
          default?: () => DashboardWidget;
        };
        if (typeof mod.default !== 'function') {
          throw new Error(
            `widget '${manifest.id}' bundle has no default export factory (expected '() => DashboardWidget')`,
          );
        }
        const widget = mod.default();
        if (widget.id !== manifest.id) {
          throw new Error(
            `widget id mismatch: manifest says '${manifest.id}', factory returned '${widget.id}'`,
          );
        }
        this.widgets.set(widget.id, widget);
        this.sources.set(widget.id, 'marketplace');
        loaded.push(widget.id);
      } catch (e) {
        console.warn(`[dashboard-widgets] failed to load '${entry.id}': ${(e as Error).message}`);
        failed.push(entry.id);
      }
    }
    return { loaded, failed };
  }

  /** Resolve a registry-relative URL the same way the viewer's PluginHost does:
   *  against the registry's own location, never the page's. */
  private resolveFromRegistry(url: string): string {
    if (/^(https?:)?\/\//.test(url) || url.startsWith('/')) return url;
    return new URL(url, new URL(this.registryUrl, window.location.href)).toString();
  }

  private async fetchManifest(url: string): Promise<PluginManifest> {
    const r = await fetch(url, { cache: 'no-cache' });  // same staleness trap as the registry
    if (!r.ok) throw new Error(`manifest fetch ${url} → HTTP ${r.status}`);
    return validateManifest(await r.json());
  }

  private assertCompatible(manifest: PluginManifest): void {
    // dashboard-widget bundles declare their host range under the
    // '@ais/dashboard-widgets' engines key. The base PluginManifest type
    // only enumerates the three viewer-side engines, so we read it as a
    // string-indexed bag here.
    const engines = manifest.engines as unknown as Record<string, string | undefined>;
    const range = engines['@ais/dashboard-widgets'];
    if (range && !satisfies(this.engineVersion, range)) {
      throw new Error(
        `widget '${manifest.id}@${manifest.version}' requires dashboard-widgets ${range}, host has ${this.engineVersion}`,
      );
    }
  }
}

function absoluteUrl(entryUrl: string, manifestUrl: string): string {
  if (/^(https?:)?\/\//.test(entryUrl) || entryUrl.startsWith('/')) return entryUrl;
  return new URL(entryUrl, new URL(manifestUrl, window.location.href)).toString();
}
